import { Button, Card, Chip, HSpacer, Text, TextLink } from '@/components/DesignSystem';
import { Table, TableRow } from '@/components/DesignSystem/Table/Table';
import { QueryKeys } from '@/constants/QueryKeys';
import { ImportErpModal } from '@/pages/Admin/HierarchyOfRetailers/Imports/ImportErpModal';
import {
  ProcessingStatusChip,
} from '@/pages/Admin/HierarchyOfRetailers/ProcessingStatusChip';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { Color } from '@/themes/MUITheme/palette';
import { ImportApi } from '@/utilities/api/ImportApi';
import { JobApi } from '@/utilities/api/JobApi';
import { PromotionApi } from '@/utilities/api/PromotionApi';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { ApiImportHistory, ApiRetailer } from '@api/interfaces';
import Autorenew from '@mui/icons-material/Autorenew';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Pagination, Stack, TableCell, TableRow as MuiTableRow, useTheme,
} from '@mui/material';
import { ApiErrors, ErrorType, ImportStatus, ImportType } from '@shared/enums';
import { ProcessingStatus } from '@shared/enums/ProcessingStatus';
import { getJsDateFromString } from '@shared/utilities';
import { stringify } from 'csv-stringify/browser/esm/sync';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

interface ErpImportsProps {
  retailer: ApiRetailer,
}

export const ErpImports = ({
  retailer,
}: ErpImportsProps) => {
  const theme = useTheme();

  const [showImportModal, setShowImportModal] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const { id } = retailer;

  const { data: importHistoryResult, isFetching: isFetchingHistory } = useQuery(
    [QueryKeys.GET_IMPORT_HISTORY_LIST, historyPage, id],
    () => ImportApi.getHistory({ limit: 5, page: historyPage, retailerId: id }),
  );

  const { data: isImportingResult } = useQuery(
    [QueryKeys.GET_IMPORT_IS_IMPORTING, id],
    () => ImportApi.getHistory({ importStatus: ImportStatus.Importing, limit: 1, retailerId: id }),
    {
      refetchInterval: (result) => (result?.total ? 5000 : false),
    },
  );

  const { data: isPendingImportResult } = useQuery(
    [QueryKeys.GET_IMPORT_IS_IMPORTING, 'pending', id],
    () => ImportApi.getHistory({ importStatus: ImportStatus.Pending, limit: 1, retailerId: id }),
    {
      refetchInterval: (result) => (result?.total ? 5000 : false),
    },
  );

  const { data: hasEligiblePromotionsForPointsEngine } = useQuery(
    [QueryKeys.GET_RETAILER_HAS_ELIGIBLE_PROMOTIONS, id],
    async () => {
      const promos = await PromotionApi.listForRetailer({
        includeExpired: true,
        limit: 1,
        retailerId: id,
      });
      return !!promos.data.length;
    },
    {
      refetchInterval: (result, _) => result ? 5000 : false,
    },
  );

  const isImporting = (
    (isImportingResult?.total ?? 0) > 0 || (isPendingImportResult?.total ?? 0) > 0
  );

  const { data: pendingImportResult } = useQuery(
    [QueryKeys.GET_IMPORT_PROCESSING_RESULT, id],
    () => ImportApi.getHistory({
      hasPointsProcessed: false,
      importStatus: ImportStatus.Complete,
      limit: 1,
      retailerId: id,
    }),
    {
      refetchInterval: (result, _) => {
        const _isAnyImportProcessing = !!result?.data.some(({ processingStatus }) => (
          processingStatus === ProcessingStatus.InProgress
        ));
        return _isAnyImportProcessing ? 5000 : false;
      },
    },
  );

  const { data: isProcessing } = useQuery(
    [QueryKeys.GET_JOB_IS_PROCESSING, id],
    () => JobApi.getIsProcessing(id),
    {
      refetchInterval: (result, _) => result ? 5000 : false,
    },
  );

  const hasActiveRewardsProgram = retailer?.rewardsPrograms?.some(({ startDate, endDate }) => {
    const currentDate = new Date();
    const startJsDate = getJsDateFromString(startDate, { timezone: 'PT' });
    const endJsDate = getJsDateFromString(endDate, { endOfDay: true, timezone: 'PT' });
    return startJsDate <= currentDate && currentDate <= endJsDate;
  });

  useEffect(() => {
    void queryClient.invalidateQueries(QueryKeys.GET_IMPORT_HISTORY_LIST);
    void queryClient.invalidateQueries(QueryKeys.GET_IMPORT_PROCESSING_RESULT);
  }, [isImporting, queryClient]);

  useEffect(() => {
    void queryClient.invalidateQueries(QueryKeys.GET_IMPORT_HISTORY_LIST);
    void queryClient.invalidateQueries(QueryKeys.GET_JOB_IS_PROCESSING);
  }, [isProcessing, queryClient]);

  const canRunPointsEngine = (
    (
      (retailer?.isProgramCreationDisabled === true && hasEligiblePromotionsForPointsEngine)
      || hasActiveRewardsProgram
    )
    && !!pendingImportResult?.total
    && !isImporting
    && isProcessing === false
  );

  const handleDownloadErrorCsv = async (history: ApiImportHistory, errorType: ErrorType) => {
    const importErrors = await ImportApi.getImportErrors(history.id, errorType);
    let csvString = '';
    if (importErrors.length) {
      csvString = stringify(importErrors, { header: true });
    }

    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `import-${errorType}s.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getImportStatusChip = (history: ApiImportHistory) => {
    let color: Color = 'success';
    let label: string = history.importStatus;
    if (history.importStatus === ImportStatus.Failed) {
      color = 'error';
    } else if (
      history.importStatus === ImportStatus.Pending
      || history.importStatus === ImportStatus.Importing
    ) {
      color = 'info';
    } else if (history.hasImportWarnings) {
      label = 'complete with warnings';
    }
    return (
      <Chip
        color={color}
        label={label}
        size="small"
        sx={{ padding: 0 }}
        testID={`${history.fileName}-${history.importStatus}-chip`}
      />
    );
  };

  const getProcessingStatusCell = (history: ApiImportHistory) => {
    const earnsPoints = [ImportType.Orders, ImportType.Bookings].includes(history.type);
    // An exclusion or a relink earns nothing itself, but it names the run that recounts points.
    const queuedItsOwnRun = !!history.processingGroupId;
    const failedBeforeProcessing = history.importStatus === ImportStatus.Failed
      && history.processingStatus === ProcessingStatus.Unprocessed;
    const hasProcessingJob = (earnsPoints || queuedItsOwnRun) && !failedBeforeProcessing;
    return (
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="flex-end"
        key={`${history.id}-processing-status`}
      >
        {hasProcessingJob ? (
          <ProcessingStatusChip
            processingStatus={history.processingStatus}
            testID={`${history.fileName}-processing-status-chip`}
          />
        ) : '—'}
      </Stack>
    );
  };

  return (
    <Card
      cardContentStyle={{ '&:last-child': { paddingBottom: 0 }, padding: 0 }}
      elevation={0}
      testID="erp-imports-card"
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        sx={{ borderBottom: `1px solid ${theme.palette.divider}`, p: '16px 20px' }}
      >
        <Text category="title-small">ERP Imports</Text>
        <Stack direction="row">
          {!retailer.isErpIntegrationEnabled && (
            <>
              <Button
                color="primary"
                disabled={!canRunPointsEngine}
                loading={isProcessing}
                onClick={async () => {
                  try {
                    await RetailerApi.processRewards(id);
                    void queryClient.invalidateQueries(QueryKeys.GET_IMPORT_HISTORY_LIST);
                    void queryClient.invalidateQueries(QueryKeys.GET_IMPORT_PROCESSING_RESULT);
                    void queryClient.invalidateQueries(QueryKeys.GET_JOB_IS_PROCESSING);
                    void queryClient.invalidateQueries(QueryKeys.GET_IMPORT_IS_IMPORTING);
                  } catch (e: any) {
                    if (
                      e.message === ApiErrors.pointsEngineAlreadyRunning) {
                      openSnackbar(e.message);
                      void queryClient.invalidateQueries(QueryKeys.GET_JOB_IS_PROCESSING);
                    } else {
                      openSnackbar('Rewards processing failed');
                    }
                  }
                }}
                square
                startIcon={<Autorenew />}
                testID="points-engine-button"
              >
                Run points engine
              </Button>
              <HSpacer size="4" />
            </>
          )}
          <Button
            disabled={isProcessing || isImporting || isFetchingHistory}
            loading={isImporting}
            onClick={() => setShowImportModal(true)}
            startIcon={<UploadIcon />}
            testID="import-erp-button"
            variant="outlined"
          >
            Import ERP data
          </Button>
        </Stack>
      </Stack>
      <Table
        headerSx={{ '&:last-of-type': { textAlign: 'right' } }}
        headerWidths={[150, 140, undefined, 260, 170]}
        headers={['Import date', 'Import type', 'File name', 'Import status', 'Processing status']}
      >
        {importHistoryResult?.total ? (
          <>
            {importHistoryResult.data.map((history) => (
              <TableRow
                key={history.id}
                values={[
                  DateTime.fromJSDate(
                    history.createdAt, { zone: 'America/New_York' },
                  ).toFormat('MMM dd, yyyy'),
                  history.type.charAt(0).toUpperCase() + history.type.slice(1),
                  history.fileName,
                  <Stack
                    alignItems="center"
                    direction="row"
                    key={`${history.id}-import-status`}
                  >
                    {getImportStatusChip(history)}
                    {(history.importStatus === ImportStatus.Failed || history.hasImportWarnings)
                      && <HSpacer size="5" />}
                    <Stack alignItems="center" direction="row">
                      {history.importStatus === ImportStatus.Failed && (
                        <TextLink
                          category="body-medium"
                          onClick={async () => {
                            await handleDownloadErrorCsv(history, ErrorType.Error);
                          }}
                          testID={`${history.id}-error-download-link`}
                        >
                          Download errors
                        </TextLink>
                      )}
                      {history.hasImportWarnings && (
                        <>
                          {history.importStatus === ImportStatus.Failed && <HSpacer size="3" />}
                          <TextLink
                            category="body-medium"
                            onClick={async () => {
                              await handleDownloadErrorCsv(history, ErrorType.Warning);
                            }}
                            testID={`${history.id}-warning-download-link`}
                          >
                            Download warnings
                          </TextLink>
                        </>
                      )}
                    </Stack>
                  </Stack>,
                  getProcessingStatusCell(history),
                ]}
              />
            ))}
          </>
        ) : (
          <MuiTableRow>
            <TableCell align="center" colSpan={5}>No ERP files have been uploaded</TableCell>
          </MuiTableRow>
        )}
      </Table>
      {!!importHistoryResult && importHistoryResult.lastPage > 0 && (
        <Stack sx={{ p: '16px 20px' }}>
          <Pagination
            count={importHistoryResult.lastPage + 1}
            onChange={(_, page) => {
              setHistoryPage(page - 1);
            }}
            page={importHistoryResult.page + 1}
            sx={{ alignSelf: 'center' }}
          />
        </Stack>
      )}
      {showImportModal && (
        <ImportErpModal
          onClose={() => setShowImportModal(false)}
          open
          retailer={retailer}
        />
      )}
    </Card>
  );
};
